'use client';

import { useEffect } from 'react';

/**
 * Hook that enables smooth scrolling for anchor links (href="#section-id")
 * This replaces the global smooth scroll behavior with targeted smooth scrolling
 * only for intentional anchor navigation.
 */
export const useSmoothAnchorScroll = () => {
  useEffect(() => {
    const handleAnchorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');

      if (!anchor) return;

      const href = anchor.getAttribute('href');

      // Check if this is an anchor link (starts with #)
      if (href?.startsWith('#')) {
        const targetId = href.slice(1);
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const scrollBehavior: ScrollBehavior = prefersReducedMotion ? 'instant' : 'smooth';

        // Empty hash or just # - scroll to top
        if (!targetId) {
          event.preventDefault();
          window.scrollTo({ top: 0, behavior: scrollBehavior });
          return;
        }

        // Find the target element
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          event.preventDefault();

          targetElement.scrollIntoView({
            behavior: scrollBehavior,
            block: 'start',
          });

          // Update the URL hash without triggering a scroll
          window.history.pushState(null, '', href);
        }
      }
    };

    // Add event listener for all clicks
    document.addEventListener('click', handleAnchorClick);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleAnchorClick);
    };
  }, []);
};
